import React, { useEffect, useState } from 'react'
import { Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import type { SeedInventory } from '../SeedInventoryTable'
import HistoryDisplay from 'views/components/SeedTracker/HistoryDisplay/HistoryDisplay'
import { drawerRowStyle, FullWidthCard } from '../../styles'

type SeedInventoryHistoryEntry = {
  created_at: string
  author_name: string
  operation: string
  action: string
  collection: string
  diff: Array<{ field_name: string; old_value: string; new_value: string }>
}

type SeedInventoryHistoryTabProps = {
  record?: SeedInventory | null
}

// Field name mapping for localization
const FIELD_NAME_MAP: Record<string, string> = {
  disposed_mt: 'disposed',
  on_hand_mt: 'onHand',
  clean_yield_actual_mt: 'cleanYieldActual',
  clean_yield_estimate_mt: 'cleanYieldEstimate',
  gross_yield_actual_bu: 'grossYieldActual',
  gross_yield_estimate_bu: 'grossYieldEstimate',
  initial_germ_actual: 'initialGermActual',
  initial_germ_estimate: 'initialGermEstimate',
  final_germ_actual: 'finalGermActual',
  final_germ_estimate: 'finalGermEstimate',
  diseased_actual: 'diseasedActual',
  diseased_estimate: 'diseasedEstimate',
  planned_seeded_area: 'plannedSeededArea',
  current_seeded_area_actual: 'currentSeededAreaActual',
  current_seeded_area_estimate: 'currentSeededAreaEstimate',
  projected_pedigree: 'projectedPedigree',
  projected_yield_actual_bu: 'projectedYieldActual',
  projected_yield_estimate_bu: 'projectedYieldEstimate',
}

const SeedInventoryHistoryTab: React.FC<SeedInventoryHistoryTabProps> = ({ record }) => {
  const { t } = useTranslation('seedTracker')
  const [entries, setEntries] = useState<SeedInventoryHistoryEntry[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!record?.id) {
      setEntries([])
      return
    }
    let cancelled = false
    setLoading(true)
    apiClient
      .get<SeedInventoryHistoryEntry[]>(endpoints.seedInventoryHistory(record.id))
      .then((data) => {
        if (!cancelled) setEntries(data ?? [])
      })
      .catch(() => {
        if (!cancelled) setEntries([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [record?.id])

  return (
    <Flex vertical style={drawerRowStyle}>
      <FullWidthCard title={t('detail.tabs.history', 'History')} variant="borderless">
        <HistoryDisplay
          entries={entries}
          loading={loading}
          empty={entries.length === 0}
          fieldNameMap={FIELD_NAME_MAP}
        />
      </FullWidthCard>
    </Flex>
  )
}

export default SeedInventoryHistoryTab
